import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { nightOwl } from "react-syntax-highlighter/dist/esm/styles/prism";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section block
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-blue-100 rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-lg text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const SafeERC20 = () => (
  <div className="relative overflow-hidden">
    {/* Render scroll progress bar and background animation */}
    <ScrollProgress />

    <div className="space-y-8 px-4 py-10 md:py-6 md:px-8">
      {/* Introduction: Animated into view */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
          type: "spring",
          stiffness: 200,
          damping: 20,
        }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto space-y-4 text-center"
      >
        <h2 className="text-3xl font-bold text-primary-dark">
          SafeERC20 and the ERC-20 Allowance Pattern in UcWaves
        </h2>
        <Paragraph className="text-gray-700 max-w-6xl mx-auto">
          ERC20 tokens form the backbone of most Ethereum-based ecosystems. Core
          functions include <code>approve()</code> and{" "}
          <code>transferFrom()</code>, enabling contracts to move user tokens in
          a controlled manner. However, not all token implementations strictly
          adhere to the ERC20 spec—some omit return values or behave
          inconsistently. OpenZeppelin’s <code>SafeERC20</code> library wraps
          standard methods in safety checks, ensuring that failures revert
          rather than silently succeed. In UcWaves, we leverage SafeERC20 across
          our Controller, StakingRewards, and SwapRouter integrations to
          guarantee reliable token flows. The following explains the classic
          allowance pattern (approve + transferFrom), the SafeERC20 enhancements
          we use in UcWaves, and best practices to avoid common pitfalls.
        </Paragraph>
      </motion.div>

      {/* Two-column layout for main content sections */}
      <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
        {/* Left column */}
        <div className="flex-1 flex flex-col space-y-6">
          <SectionCard title="1. The ERC20 Allowance Pattern">
            <div className="mb-2">
              <strong>1.1 Approve and TransferFrom</strong>
            </div>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                <code>approve(spender, amount)</code>: A token holder grants{" "}
                <code>spender</code> permission to transfer up to{" "}
                <code>amount</code> tokens on their behalf.
              </li>
              <br />
              <li>
                <code>transferFrom(from, to, amount)</code>: Once approved, the{" "}
                <code>spender</code> contract calls this function to pull tokens
                from <code>from</code> into <code>to</code>. The allowance is
                reduced by <code>amount</code>.
              </li>
            </ul>
            <Paragraph>
              UcWaves uses this two-step flow whenever users interact with the
              Controller (burning NAU), StakingRewards (locking tokens), or
              SwapRouter (liquidity operations). Requiring explicit{" "}
              <code>approve()</code> calls ensures that UcWaves only moves the
              tokens a user intends to spend.
            </Paragraph>

            <div className="mt-6 mb-2">
              <strong>1.2 Known ERC20 Shortcomings</strong>
            </div>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                Some tokens do not return a boolean on <code>transfer</code> or{" "}
                <code>approve</code>, violating the standard.
              </li>
              <br />
              <li>
                Others return false on failure instead of reverting, causing
                silent failures downstream.
              </li>
            </ul>
            <Paragraph>
              Such inconsistencies can break contracts expecting strict
              behavior, leading to stuck approvals or unaccounted token
              movements—issues we avoid in UcWaves by standardizing on
              SafeERC20.
            </Paragraph>
          </SectionCard>

          <SectionCard title="2. OpenZeppelin’s SafeERC20 Library in UcWaves">
            <Paragraph>
              SafeERC20 provides wrappers that handle nonstandard ERC20 tokens.
              Under the hood, it uses low-level calls and checks for success:
            </Paragraph>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                <code>safeTransfer(token, to, amount)</code>
              </li>
              <br />
              <li>
                <code>safeTransferFrom(token, from, to, amount)</code>
              </li>
              <br />
              <li>
                <code>safeApprove(token, spender, amount)</code>
              </li>
              <li>
                <code>safeIncreaseAllowance(token, addedValue)</code>
              </li>
              <br />
              <li>
                <code>safeDecreaseAllowance(token, subtractedValue)</code>
              </li>
            </ul>
            <Paragraph>
              In UcWaves, our Solidity contracts (Controller.sol,
              StakingRewards.sol, and SwapRouter.sol) import and{" "}
              <code>using SafeERC20 for IERC20</code> to ensure every token pull
              or push reverts on failure—protecting user funds from misbehaving
              tokens.
            </Paragraph>
          </SectionCard>
        </div>

        {/* Right column */}
        <div className="flex-1 flex flex-col space-y-6">
          <SectionCard title="3. Integrating SafeERC20 in UcWaves Contracts">
            <div className="mb-2">
              <strong>3.1 Import and Usage</strong>
            </div>
            <SyntaxHighlighter
              language="solidity"
              style={nightOwl}
              customStyle={{
                fontSize: "0.75rem",
                padding: "8px",
                background: "#011627",
              }}
            >
              {`import "@openzeppelin/contracts/token/ERC20/utils/SafeERC20.sol";
import "@openzeppelin/contracts/token/ERC20/IERC20.sol";

contract Controller {
  using SafeERC20 for IERC20;
  IERC20 public immutable NAU;

  constructor(IERC20 _nau) {
    NAU = _nau;
  }

  function transformX(uint256 amount) external {
    // Pull NAU safely from user in UcWaves
    NAU.safeTransferFrom(msg.sender, address(this), amount);
    // ... other logic (TWAP, minting NAUY and NAUN)
  }
}`}
            </SyntaxHighlighter>

            <div className="mt-4 mb-2">
              <strong>3.2 Avoiding Allowance Race Conditions</strong>
            </div>
            <ul className="list-disc list-inside text-base text-gray-800 mb-2">
              <li>
                Changing allowances directly can lead to the ERC20 race
                condition: if a spender frontruns a change, they could spend
                both old and new amounts.
              </li>
              <br />
              <li>
                UcWaves mitigates this by recommending incremental adjustments
                using: <code>safeIncreaseAllowance</code> and{" "}
                <code>safeDecreaseAllowance</code>, or by resetting to zero
                before setting a new allowance.
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="4. Best Practices and Security Considerations in UcWaves text-justify">
            <ul className="list-disc list-inside text-base text-gray-800 text-justify">
              <li>
                <strong>One-Time Approvals:</strong> For recurring actions
                (e.g., multiple staking operations), the UI encourages a single
                high-value <code>approve</code>, minimizing repeated gas costs.
              </li>
              <br />
              <li>
                <strong>Allowance Minimization:</strong> Where possible, UcWaves
                limits allowances to the exact amount needed per interaction.
              </li>
              <br />
              <li>
                <strong>Clear Error Messaging:</strong> SafeERC20 bubbles up
                revert reasons (e.g., "SafeERC20: low-level call failed"), and
                the front end surfaces these messages to help users diagnose
                issues.
              </li>
              <br />
              <li>
                <strong>Immutable Contracts:</strong> After audits, UcWaves
                locks the Controller and related contracts to cement the token
                flow logic.
              </li>
              <br />
              <li>
                <strong>Prevent centralized control:</strong> To promote broad
                participation and prevent centralized control, NAU — as the
                governance token of the ecosystem — includes key protective
                limits:
                <ul>
                  <li>
                    <strong>Max Transaction:</strong> 10,000,000 NAU
                  </li>
                  <li>
                    <strong>Max Wallet:</strong> 13% of total supply (130,000,000
                    NAU)
                  </li>
                </ul>
                These limits are designed to prevent large holders (or “whales”)
                from dominating the ecosystem and ensure governance decisions
                reflect a diverse community of holders.
              </li>
            </ul>
          </SectionCard>
        </div>
      </div>

      {/* Conclusion */}
      <Paragraph className="text-gray-700 max-w-6xl mx-auto text-justify">
        By combining the ERC20 <code>approve</code> + <code>transferFrom</code>{" "}
        pattern with OpenZeppelin’s SafeERC20 wrappers, UcWaves ensures a
        secure, reliable token interaction model. Users grant explicit
        permission, token movements never fail silently, and common allowance
        pitfalls are addressed—forming the foundation for safe burning, staking,
        and swapping in Uncensored Waves.
      </Paragraph>
    </div>
  </div>
);

export default SafeERC20;
